import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, X, Send, User, Headphones, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'realprop_support_session';
const HISTORY_KEY = 'realprop_support_history';

// Routes where we do NOT want to show the floating widget.
const HIDE_ON_PATHS = ['/admin/support'];

const buildSessionId = () => {
  // Stable per-browser session id (anonymous users still get one).
  const existing = (typeof window !== 'undefined') && window.localStorage.getItem(STORAGE_KEY);
  if (existing) return existing;
  const sid = `sup_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  try { window.localStorage.setItem(STORAGE_KEY, sid); } catch (_e) {}
  return sid;
};

export const SupportChatWidget = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escEmail, setEscEmail] = useState('');
  const [escSubject, setEscSubject] = useState('');
  const sessionId = useRef(buildSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    // Restore prior session history from localStorage
    try {
      const raw = window.localStorage.getItem(HISTORY_KEY);
      if (raw) setMessages(JSON.parse(raw));
    } catch (_e) {}
  }, []);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    try { window.localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-30))); } catch (_e) {}
  }, [messages, open]);

  // Pre-fill escalation email when user is logged in
  useEffect(() => {
    if (user?.email && !escEmail) setEscEmail(user.email);
  }, [user, escEmail]);

  if (HIDE_ON_PATHS.some(p => location.pathname.startsWith(p))) return null;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API_BASE}/api/support/chat`, {
        session_id: sessionId.current,
        message: text,
        user_id: user?.user_id || null,
        user_email: user?.email || null,
      });
      const reply = res.data?.reply || "I'm not sure — try rephrasing or tap 'Talk to a human'.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble right now. Tap 'Talk to a human' and our team will get back to you within 24 hours.",
      }]);
    } finally {
      setSending(false);
    }
  };

  const submitEscalation = async () => {
    if (!escEmail) return;
    setEscalating(true);
    try {
      await axios.post(`${API_BASE}/api/support/escalate`, {
        session_id: sessionId.current,
        contact_email: escEmail,
        contact_name: user?.name || null,
        subject: escSubject || null,
        user_id: user?.user_id || null,
      });
      setMessages(prev => [...prev, {
        role: 'system',
        content: "Got it! Our team will reply by email within 24 hours. You can close this chat now.",
      }]);
      setShowEscalate(false);
      setEscSubject('');
    } catch (_err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: "We couldn't open the ticket right now. Email us at support@realprop.io and we'll respond fast.",
      }]);
    } finally {
      setEscalating(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* ───── Launcher button ───── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[9998] h-14 w-14 rounded-full bg-black text-white shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center group"
          aria-label="Open support chat"
          data-testid="support-chat-launcher"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-emerald-500 rounded-full ring-2 ring-white" />
        </button>
      )}

      {/* ───── Chat window ───── */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[9998] w-[360px] max-w-[calc(100vw-32px)] h-[540px] max-h-[calc(100vh-32px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-black to-gray-800 text-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-amber-400/20 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-amber-300" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold">RealProp Support</p>
                <p className="text-[10px] text-emerald-300 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 bg-emerald-400 rounded-full inline-block" />
                  Online · AI first, human if needed
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-300 hover:text-white"
              aria-label="Close"
              data-testid="support-chat-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center py-8 text-gray-500">
                <Sparkles className="h-6 w-6 mb-2 text-amber-500" />
                <p className="text-sm font-semibold text-gray-700 mb-1">Hi! How can we help?</p>
                <p className="text-xs">Ask about the Chrome extension, brand kit, templates, pricing — or anything else.</p>
              </div>
            )}
            {messages.map((m, i) => {
              if (m.role === 'system') {
                return (
                  <div key={i} className="flex justify-center" data-testid="support-message-system">
                    <div className="px-3 py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">
                      {m.content}
                    </div>
                  </div>
                );
              }
              const mine = m.role === 'user';
              return (
                <div key={i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`support-message-${m.role}`}>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      mine
                        ? 'bg-black text-white rounded-br-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl bg-white border border-gray-200 text-gray-500 text-sm flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Escalation panel */}
          {showEscalate && (
            <div className="px-4 py-3 bg-amber-50 border-t border-amber-200 space-y-2 flex-shrink-0" data-testid="support-escalate-panel">
              <p className="text-xs font-semibold text-amber-900">Talk to a human</p>
              <input
                type="email"
                placeholder="Your email"
                value={escEmail}
                onChange={(e) => setEscEmail(e.target.value)}
                className="w-full h-8 px-2 rounded-md border border-amber-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="support-escalate-email"
              />
              <input
                type="text"
                placeholder="Subject (optional)"
                value={escSubject}
                onChange={(e) => setEscSubject(e.target.value)}
                className="w-full h-8 px-2 rounded-md border border-amber-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="support-escalate-subject"
              />
              <div className="flex gap-2">
                <button
                  onClick={submitEscalation}
                  disabled={!escEmail || escalating}
                  className="flex-1 h-8 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center justify-center gap-1.5"
                  data-testid="support-escalate-submit"
                >
                  {escalating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Headphones className="h-3 w-3" />}
                  Send to team
                </button>
                <button
                  onClick={() => setShowEscalate(false)}
                  className="h-8 px-3 rounded-md border border-amber-300 text-amber-800 text-xs hover:bg-amber-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Input bar */}
          <div className="border-t border-gray-200 bg-white p-3 flex items-end gap-2 flex-shrink-0">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Type your question…"
              className="flex-1 resize-none px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black/40 max-h-24"
              disabled={sending}
              data-testid="support-chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="h-9 w-9 rounded-xl bg-black text-white disabled:opacity-30 hover:bg-gray-800 flex items-center justify-center flex-shrink-0"
              aria-label="Send"
              data-testid="support-chat-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>

          {/* Footer — escalate trigger */}
          {!showEscalate && (
            <div className="px-3 pb-3 -mt-1 flex-shrink-0">
              <button
                onClick={() => setShowEscalate(true)}
                className="text-[11px] text-gray-500 hover:text-black flex items-center gap-1 mx-auto"
                data-testid="support-escalate-trigger"
              >
                <User className="h-3 w-3" />
                Not enough? Talk to a human →
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SupportChatWidget;
